import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// API Types
export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/admin/stats'),
  getRecentActivity: () => api.get('/admin/activity/recent'),
  getRealTimeMetrics: () => api.get('/admin/metrics/realtime'),
  getChartData: (period: string) => api.get(`/admin/charts/${period}`),
};

// Users API
export const usersApi = {
  getUsers: (params: any) => api.get('/admin/users', { params }),
  getUser: (id: string) => api.get(`/admin/users/${id}`),
  toggleBan: (id: string, banReason?: string) => api.put(`/admin/users/${id}/ban`, { banReason }),
  banUser: (id: string, reason?: string) => api.put(`/admin/users/${id}/ban`, { banReason: reason }),
  unbanUser: (id: string) => api.put(`/admin/users/${id}/ban`),
  adjustBalance: (id: string, data: any) => api.put(`/admin/users/${id}/balance`, data),
  getUserActivity: (id: string) => api.get(`/admin/users/${id}/activity`),
  getUserTransactions: (id: string, params?: any) => api.get(`/admin/users/${id}/transactions`, { params }),
  getUserGames: (id: string, params?: any) => api.get(`/admin/users/${id}/games`, { params }),
  exportUsers: (params?: any) => api.get('/admin/users/export', { params, responseType: 'blob' }),
};

// KYC API
export const kycApi = {
  getApplications: (params: any) => api.get('/admin/kyc', { params }),
  getApplication: (id: string) => api.get(`/admin/kyc/${id}`),
  approveKYC: (id: string, notes?: string) => api.put(`/admin/kyc/${id}/approve`, { notes }),
  rejectKYC: (id: string, reason: string) => api.put(`/admin/kyc/${id}/reject`, { reason }),
  requestResubmission: (id: string, reason: string) => api.put(`/admin/kyc/${id}/request-revision`, { reason }),
  getKYCStats: () => api.get('/admin/kyc/stats'),
  getKYCSettings: () => api.get('/admin/kyc/settings'),
  updateKYCSettings: (data: any) => api.put('/admin/kyc/settings', data),
};

// Transactions API
export const transactionsApi = {
  getTransactions: (params: any) => api.get('/admin/transactions', { params }),
  getTransaction: (id: string) => api.get(`/admin/transactions/${id}`),
  getPendingWithdrawals: (params?: any) => api.get('/admin/withdrawals/pending', { params }),
  approveWithdrawal: (id: string, notes?: string) => api.put(`/admin/withdrawals/${id}/approve`, { notes }),
  rejectWithdrawal: (id: string, reason: string) => api.put(`/admin/withdrawals/${id}/reject`, { reason }),
  getTransactionStats: () => api.get('/admin/transactions/stats'),
  exportTransactions: (params?: any) => api.get('/admin/transactions/export', { params, responseType: 'blob' }),
};

// Games API
export const gamesApi = {
  getRooms: (params: any) => api.get('/admin/games/rooms', { params }),
  getRoom: (id: string) => api.get(`/admin/games/rooms/${id}`),
  getLudoGames: (params?: any) => api.get('/admin/games/ludo', { params }),
  getGameStats: () => api.get('/admin/games/stats'),
  resolveDispute: (disputeId: string, data: { resolution: string; resolutionNote?: string; winnerId?: string }) =>
    api.put(`/admin/games/disputes/${disputeId}/resolve`, data),
  cancelGame: (id: string, reason: string) => api.put(`/admin/games/${id}/cancel`, { reason }),
  getGameDisputes: (params?: any) => api.get('/admin/games/disputes', { params }),
};

// Referrals API
export const referralsApi = {
  getReferrals: (params: any) => api.get('/admin/referrals', { params }),
  getReferralStats: () => api.get('/admin/referrals/stats'),
  getTopReferrers: (limit?: number) => api.get('/admin/referrals/top', { params: { limit } }),
  updateReferralSettings: (data: any) => api.put('/admin/referrals/settings', data),
};

// Notifications API
export const notificationsApi = {
  getNotifications: (params?: any) => api.get('/admin/notifications', { params }),
  sendNotification: (data: any) => api.post('/admin/notifications/send', data),
  sendBulkNotification: (data: any) => api.post('/admin/notifications/bulk', data),
  getTemplates: () => api.get('/admin/notifications/templates'),
  createTemplate: (data: any) => api.post('/admin/notifications/templates', data),
  updateTemplate: (id: string, data: any) => api.put(`/admin/notifications/templates/${id}`, data),
  deleteTemplate: (id: string) => api.delete(`/admin/notifications/templates/${id}`),
};

// Support API
export const supportApi = {
  getTickets: (params?: any) => api.get('/admin/support/tickets', { params }),
  getTicket: (id: string) => api.get(`/admin/support/tickets/${id}`),
  updateTicketStatus: (id: string, status: string) => api.put(`/admin/support/tickets/${id}/status`, { status }),
  addTicketReply: (id: string, message: string) => api.post(`/admin/support/tickets/${id}/reply`, { message }),
  assignTicket: (id: string, adminId: string) => api.put(`/admin/support/tickets/${id}/assign`, { adminId }),
  getTicketStats: () => api.get('/admin/support/stats'),
  getFAQs: (params?: any) => api.get('/admin/support/faqs', { params }),
  createFAQ: (data: any) => api.post('/admin/support/faqs', data),
  updateFAQ: (id: string, data: any) => api.put(`/admin/support/faqs/${id}`, data),
  deleteFAQ: (id: string) => api.delete(`/admin/support/faqs/${id}`),
};

// Reports API
export const reportsApi = {
  getRevenueReport: (params: any) => api.get('/admin/reports/revenue', { params }),
  getUserReport: (params: any) => api.get('/admin/reports/users', { params }),
  getGameReport: (params: any) => api.get('/admin/reports/games', { params }),
  getFinancialReport: (params: any) => api.get('/admin/reports/financial', { params }),
  generateReport: (type: string, params: any) => api.post(`/admin/reports/${type}`, params),
  exportReport: (type: string, params: any) =>
    api.get(`/admin/reports/${type}/export`, { params, responseType: 'blob' }),
};

// Settings API
export const settingsApi = {
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data: any) => api.put('/admin/settings', data),
  getAdminUsers: () => api.get('/admin/users/admins'),
  createAdminUser: (data: any) => api.post('/admin/users/admins', data),
  updateAdminUser: (id: string, data: any) => api.put(`/admin/users/admins/${id}`, data),
  deleteAdminUser: (id: string) => api.delete(`/admin/users/admins/${id}`),
  getSystemHealth: () => api.get('/admin/system/health'),
};

// Activity API
export const activityApi = {
  getActivities: (params?: any) => api.get('/admin/activities', { params }),
  getStats: () => api.get('/admin/activities/stats'),
};

// Leaderboard API
export const leaderboardApi = {
  getLeaderboard: (params?: any) => api.get('/leaderboard', { params }),
  getTopWinners: (period?: string) => api.get('/leaderboard/winners', { params: { period } }),
  getTopReferrers: () => api.get('/leaderboard/referrers'),
};

// ============================================================================
// QR MANAGEMENT & DEPOSIT VERIFICATION APIs
// ============================================================================

// QR Management API
export const qrManagementApi = {
  uploadQR: (data: { qrImageUrl: string; upiId: string; merchantName?: string }) =>
    api.post('/admin/payment/qr', data),
  getHistory: (params?: any) => api.get('/admin/payment/qr/history', { params }),
  getActive: () => api.get('/admin/payment/qr/active'),
  deleteQR: (id: string) => api.delete(`/admin/payment/qr/${id}`),
};

// Deposits API (for admin verification)
export const depositsApi = {
  getPending: (params?: any) => api.get('/admin/deposits/pending', { params }),
  getAll: (params?: any) => api.get('/admin/deposits', { params }),
  verify: (id: string, data: { action: 'approve' | 'reject'; notes?: string }) =>
    api.put(`/admin/deposits/${id}/verify`, data),
};
